import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { formatDate, formatIsoDay, formatRate } from "@/lib/format";

const STYLES = { USD: "text-blue-700", EUR: "text-indigo-700", CNY: "text-amber-700" };

export function useRates(intervalMs = 10 * 60 * 1000) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/currency/rates").then(({ data }) => alive && setData(data)).catch(() => {});
    load();
    const t = setInterval(load, intervalMs);
    return () => { alive = false; clearInterval(t); };
  }, [intervalMs]);
  return [data, setData];
}

export default function CurrencyTicker() {
  const [data] = useRates();
  if (!data) return <div className="hidden md:block h-9 w-64 rounded-md bg-slate-100 animate-pulse" />;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="hidden md:flex items-center gap-1 h-9 px-1 rounded-md border border-slate-200 bg-white" data-testid="currency-ticker">
            {["USD", "EUR", "CNY"].map((c) => (
              <div key={c} className="flex items-baseline gap-1.5 px-2 border-r last:border-r-0 border-slate-100" data-testid={`currency-rate-${c.toLowerCase()}`}>
                <span className={`text-[10px] font-bold ${STYLES[c]}`}>{c}</span>
                <span className="font-mono text-[12px] font-medium text-slate-800 num">{formatRate(data.rates?.[c]?.forex_selling)}</span>
              </div>
            ))}
            {data.stale && (
              <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 animate-pulse" data-testid="currency-stale-badge">
                <AlertTriangle className="h-3 w-3" />Son bilinen kur
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent className="text-xs max-w-xs">
          <div className="font-semibold">TCMB Döviz Satış Kurları</div>
          <div>Bülten: {formatIsoDay(data.date)} {data.bulletin_no ? `(${data.bulletin_no})` : ""}</div>
          <div>Güncelleme: {formatDate(data.fetched_at)}</div>
          {data.warning && <div className="text-amber-600 mt-1">{data.warning}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
